import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useNavigate } from 'react-router-dom';
import { Database, Download, RefreshCw, FileText, Users, CreditCard, Mail, ChevronDown, ChevronUp, BarChart3, Lock } from 'lucide-react';
import CorporateHeader from './CorporateHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(() => !!sessionStorage.getItem('admin_token'));
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const [activeCollection, setActiveCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError('');
    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        sessionStorage.setItem('admin_token', data.token);
        setAuthenticated(true);
      } else {
        setPinError('Invalid password. Try again.');
      }
    } catch {
      setPinError('Connection error. Try again.');
    } finally {
      setVerifying(false);
    }
  };

  const collectionMeta = {
    project_inquiries: { label: 'Project Inquiries', icon: FileText, color: 'text-blue-400', bg: 'bg-blue-900/30 border-blue-800' },
    ghostwriting_intakes: { label: 'Ghostwriting Intakes', icon: FileText, color: 'text-purple-400', bg: 'bg-purple-900/30 border-purple-800' },
    eblackbook_signups: { label: 'Business Directory Signups', icon: Users, color: 'text-green-400', bg: 'bg-green-900/30 border-green-800' },
    payment_transactions: { label: 'Payment Transactions', icon: CreditCard, color: 'text-yellow-400', bg: 'bg-yellow-900/30 border-yellow-800' },
    waitlist: { label: 'Waitlist Signups', icon: Mail, color: 'text-red-400', bg: 'bg-red-900/30 border-red-800' },
    photoshoot_orders: { label: 'AI Photoshoot Orders', icon: CreditCard, color: 'text-amber-400', bg: 'bg-amber-900/30 border-amber-800' },
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [statusRes, submissionsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/db-status`, { headers }),
        fetch(`${API_URL}/api/admin/submissions`, { headers })
      ]);
      if (statusRes.status === 401 || submissionsRes.status === 401) {
        sessionStorage.removeItem('admin_token');
        setAuthenticated(false);
        return;
      }
      const statusData = await statusRes.json();
      const submissionsData = await submissionsRes.json();
      setDbStatus(statusData);
      setSubmissions(submissionsData.data);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) fetchData();
  }, [authenticated]);

  const handleExport = async (collectionName) => {
    setExporting(collectionName);
    try {
      const res = await fetch(`${API_URL}/api/admin/export/${collectionName}`, { headers: getAuthHeaders() });
      if (res.status === 401) { sessionStorage.removeItem('admin_token'); setAuthenticated(false); return; }
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collectionName}_export.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(null);
    }
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') return JSON.stringify(val);
    if (typeof val === 'string' && val.length > 80) return val.substring(0, 80) + '...';
    return String(val);
  };

  const totalRecords = dbStatus ? (dbStatus.total_records || 0) : 0;

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 w-full max-w-sm text-center">
          <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Admin Access</h1>
          <p className="text-gray-400 text-sm mb-6">Enter your password to continue</p>
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <Input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter password"
              className="bg-black border-gray-700 text-white text-center text-lg"
              autoFocus
              data-testid="admin-pin-input"
            />
            {pinError && <p className="text-red-400 text-sm">{pinError}</p>}
            <Button type="submit" disabled={verifying || !pin} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold" data-testid="admin-pin-submit">
              {verifying ? 'Verifying...' : 'Enter'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white" data-testid="admin-title">Admin Dashboard</h1>
              <p className="text-gray-400 mt-1">View submissions, transactions, and export data</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <RefreshCw className="w-10 h-10 text-gray-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading dashboard data...</p>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
                  <Database className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{dbStatus?.connected ? 'Online' : 'Offline'}</div>
                  <div className="text-xs text-gray-500">Database</div>
                </div>
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
                  <BarChart3 className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{totalRecords}</div>
                  <div className="text-xs text-gray-500">Total Records</div>
                </div>
                {submissions && Object.entries(collectionMeta).map(([key, meta]) => {
                  const Icon = meta.icon;
                  const count = submissions[key]?.count || 0;
                  return (
                    <div key={key} className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
                      <Icon className={`w-6 h-6 ${meta.color} mx-auto mb-2`} />
                      <div className="text-2xl font-bold text-white">{count}</div>
                      <div className="text-xs text-gray-500 truncate">{meta.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Collection Tables */}
              {submissions && Object.entries(collectionMeta).map(([key, meta]) => {
                const data = submissions[key];
                if (!data || data.count === 0) return null;
                const isOpen = activeCollection === key;
                const Icon = meta.icon;
                const records = data.records || [];
                const columns = records.length > 0 ? Object.keys(records[0]).filter(k => !['_id', 'id'].includes(k)).slice(0, 8) : [];

                return (
                  <div key={key} className={`mb-4 border rounded-xl overflow-hidden ${meta.bg}`}>
                    {/* Collection Header */}
                    <div 
                      className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors cursor-pointer"
                      data-testid={`collection-${key}`}
                    >
                      <div className="flex items-center gap-3" onClick={() => setActiveCollection(isOpen ? null : key)}>
                        <Icon className={`w-5 h-5 ${meta.color}`} />
                        <span className="text-white font-semibold">{meta.label}</span>
                        <span className="text-gray-500 text-sm">({data.count} records)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleExport(key)}
                          disabled={exporting === key}
                          className="bg-gray-800 hover:bg-gray-700 text-white text-xs"
                          data-testid={`export-${key}`}
                        >
                          <Download className={`w-3 h-3 mr-1 ${exporting === key ? 'animate-spin' : ''}`} />
                          {exporting === key ? 'Exporting...' : 'CSV'}
                        </Button>
                        <div onClick={() => setActiveCollection(isOpen ? null : key)} className="cursor-pointer">
                          {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Table */}
                    {isOpen && records.length > 0 && (
                      <div className="overflow-x-auto border-t border-gray-800">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-black/50">
                              {columns.map(col => (
                                <th key={col} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                  {col.replace(/_/g, ' ')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {records.slice(0, 20).map((record, idx) => (
                              <tr key={idx} className="border-t border-gray-800/50 hover:bg-white/5">
                                {columns.map(col => (
                                  <td key={col} className="px-4 py-3 text-gray-300 whitespace-nowrap max-w-[200px] truncate">
                                    {formatValue(record[col])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {records.length > 20 && (
                          <div className="p-3 text-center text-gray-500 text-sm border-t border-gray-800/50">
                            Showing 20 of {records.length} records. Export CSV for full data.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
